import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useAccessibility } from "./AccessibilityContext";

const ClaraContext = createContext(null);

const PREFERRED_VOICE_NAMES = [
    "Samantha",
    "Microsoft Zira",
    "Microsoft Zira - English (United States)",
    "Microsoft Zira Desktop",
    "Google US English",
    "Google US English Female",
    "Karen",
    "Tessa",
    "Allison",
];

function pickVoice(voices) {
    if (!voices || voices.length === 0) return null;
    for (const name of PREFERRED_VOICE_NAMES) {
        const exact = voices.find((v) => v.name === name);
        if (exact) return exact;
        const partial = voices.find((v) =>
            v.name.toLowerCase().includes(name.toLowerCase()),
        );
        if (partial) return partial;
    }
    // Prefer any en-US voice with "female" hint, else first en-US, else first available
    const enUS = voices.filter((v) => /en[-_]US/i.test(v.lang));
    const female = enUS.find((v) => /female|woman|samantha|zira|karen|tessa|allison|google us english/i.test(v.name));
    return female || enUS[0] || voices[0];
}

export function ClaraProvider({ children }) {
    const [toast, setToast] = useState(null); // { id, text }
    const [isSpeaking, setIsSpeaking] = useState(false);
    const voiceRef = useRef(null);
    const toastTimerRef = useRef(null);
    const utteranceRef = useRef(null);
    const { quietActive } = useAccessibility();
    const quietRef = useRef(quietActive);
    quietRef.current = quietActive;
    const supported =
        typeof window !== "undefined" && "speechSynthesis" in window;

    useEffect(() => {
        if (!supported) return;
        const synth = window.speechSynthesis;

        const loadVoices = () => {
            const voices = synth.getVoices();
            if (voices && voices.length) {
                voiceRef.current = pickVoice(voices);
            }
        };
        loadVoices();
        synth.onvoiceschanged = loadVoices;

        return () => {
            try {
                synth.cancel();
            } catch (err) {
                console.warn("speechSynthesis cancel (on unmount) failed:", err);
            }
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, [supported]);

    const showToast = useCallback((text) => {
        if (!text) return;
        const id = Date.now() + Math.random();
        setToast({ id, text });
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => {
            setToast((current) => (current && current.id === id ? null : current));
        }, 6000);
    }, []);

    const dismissToast = useCallback(() => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast(null);
    }, []);

    const speak = useCallback(
        (text, options = {}) => {
            if (!text) return;
            // Always surface visual toast for accessibility (hearing impairment / preference).
            showToast(text);
            const onEnd = options.onEnd;
            if (!supported) {
                setIsSpeaking(false);
                if (onEnd) setTimeout(onEnd, 250);
                return;
            }
            const synth = window.speechSynthesis;
            try {
                synth.cancel();
            } catch (err) {
                console.warn("speechSynthesis cancel (pre-speak) failed:", err);
            }
            const u = new SpeechSynthesisUtterance(text);
            const quiet = quietRef.current;
            u.rate = options.rate ?? (quiet ? 0.82 : 0.95);
            u.pitch = options.pitch ?? (quiet ? 0.95 : 1.05);
            u.volume = options.volume ?? (quiet ? 0.6 : 1.0);
            u.lang = "en-US";
            if (voiceRef.current) u.voice = voiceRef.current;
            u.onstart = () => setIsSpeaking(true);
            u.onend = () => {
                setIsSpeaking(false);
                if (onEnd) onEnd();
            };
            u.onerror = () => {
                setIsSpeaking(false);
                if (onEnd) onEnd();
            };
            utteranceRef.current = u;
            try {
                synth.speak(u);
            } catch (err) {
                console.warn("speechSynthesis speak failed:", err);
                setIsSpeaking(false);
                if (onEnd) onEnd();
            }
        },
        [showToast, supported],
    );

    const stop = useCallback(() => {
        if (supported) {
            try {
                window.speechSynthesis.cancel();
            } catch (err) {
                console.warn("speechSynthesis cancel (stop) failed:", err);
            }
        }
        setIsSpeaking(false);
    }, [supported]);

    const value = useMemo(
        () => ({ speak, stop, toast, isSpeaking, supported, dismissToast }),
        [speak, stop, toast, isSpeaking, supported, dismissToast],
    );

    return (
        <ClaraContext.Provider value={value}>{children}</ClaraContext.Provider>
    );
}

export function useClara() {
    const ctx = useContext(ClaraContext);
    if (!ctx) throw new Error("useClara must be used within ClaraProvider");
    return ctx;
}
